/** A quote line is not necessarily an independent physical AV device --
 *  a software licence, a bulk-materials allowance, a mount/fixing, a
 *  bare cable, a labour-only line, or a client-supplied reference item
 *  are all real BOM lines that must never be treated like a genuine
 *  unclassified physical product. This module is the one place that
 *  question gets answered, consumed by fetchRoomPortBoard (which schematic
 *  nodes reach Design & Schematics at all) and ConnectionsPanel (which of
 *  those nodes can usefully be asked "Power unknown -- set it").
 *
 *  Every enum value here corresponds 1:1 to the physicality taxonomy
 *  (ACTIVE_DEVICE, PASSIVE_DEVICE, ACCESSORY, ASSEMBLY_COMPONENT,
 *  MOUNTING, CABLE, CONSUMABLE, SOFTWARE_LICENCE,
 *  CLIENT_SUPPLIED_REFERENCE, LABOUR_MISC, UNKNOWN_PHYSICAL_DEVICE),
 *  cased in lower_snake_case to match every other enum already in this
 *  schema (equipment_category, av_equipment_role, power_method, ...).
 *  The matching Postgres type is public.equipment_physicality
 *  (av_product_profiles.physicality) -- a canonical, "ask once" answer
 *  that always wins when a product has been curated, the same rule
 *  power_method already follows (0454). */
export type EquipmentPhysicality =
  | "active_device"
  | "passive_device"
  | "accessory"
  | "assembly_component"
  | "mounting"
  | "cable"
  | "consumable"
  | "software_licence"
  | "client_supplied_reference"
  | "labour_misc"
  | "unknown_physical_device";

/** Physicalities that never belong on the Equipment/Connections port
 *  board as their own schematic node. None of these is placed, powered,
 *  or wired the way a real AV device is -- showing them there is exactly
 *  the "Not categorised" / "Unassigned equipment" / "Power unknown"
 *  false-positive this module exists to stop (confirmed live on job 103,
 *  room 201: an Embed Digital Signage annual licence, a "Misc" fixings
 *  line, a "Cables, connectors and consumables" line, a "Lectern to be
 *  supplied by client" line -- none of them a physical AV device). An
 *  accessory (mount, bracket, trolley) already had its own narrower
 *  category-based filter (api.ts fetchRoomPortBoard, 0224); this is the
 *  general-purpose successor, keyed on the richer physicality signal
 *  rather than only `category === "accessory"` -- kept alongside it
 *  rather than replacing it, since not every accessory-shaped row has
 *  been reclassified yet. */
const NOT_A_SCHEMATIC_NODE: ReadonlySet<EquipmentPhysicality> = new Set([
  "accessory",
  "mounting",
  "cable",
  "consumable",
  "software_licence",
  "client_supplied_reference",
  "labour_misc",
]);

/** True unless this physicality means the row should never appear as its
 *  own device on the Equipment/Connections port board. `null` (nothing
 *  has classified this row at all) stays visible -- the same
 *  conservative default the board already applies to a genuinely
 *  unmatched product today; this module only ever narrows what's
 *  excluded, never widens it beyond a physicality it actually knows. */
export function isSchematicNodePhysicality(physicality: string | null): boolean {
  return physicality == null || !NOT_A_SCHEMATIC_NODE.has(physicality as EquipmentPhysicality);
}

/** Physicalities for which "Power unknown -- set it" can never be a
 *  useful action, even on the rare row that reaches the board despite
 *  one of them (shouldn't happen once isSchematicNodePhysicality is
 *  applied upstream, but this stays correct on its own regardless): a
 *  passive device already answers its own power question by definition,
 *  and an assembly component's power (a lavalier capsule's, an
 *  antenna's) belongs to whatever it plugs into, not to itself. Superset
 *  of NOT_A_SCHEMATIC_NODE. */
const POWER_NOT_APPLICABLE: ReadonlySet<EquipmentPhysicality> = new Set([
  ...NOT_A_SCHEMATIC_NODE,
  "assembly_component",
  "passive_device",
]);

/** True unless this physicality makes a power question meaningless.
 *  `null` stays eligible -- an unclassified row keeps behaving exactly
 *  as it does today (a real candidate for "Power unknown"), and
 *  UNKNOWN_PHYSICAL_DEVICE / ACTIVE_DEVICE both stay eligible too: a
 *  real Q-SYS Core with power_method left at "unknown", or a genuinely
 *  unmatched device nobody has looked at yet, are exactly the cases this
 *  warning still needs to catch. */
export function isPowerWarningApplicable(physicality: string | null): boolean {
  return physicality == null || !POWER_NOT_APPLICABLE.has(physicality as EquipmentPhysicality);
}

/** Same question, for the "not placed" / "Unassigned equipment" family
 *  of location warnings. Identical to node visibility today -- a row
 *  that isn't a schematic node never gets asked where it lives either --
 *  kept as its own named export because the two questions (LOCATION
 *  APPLICABILITY in the spec) are conceptually distinct even though
 *  today's answer set happens to be the same set of physicalities. */
export function isLocationWarningApplicable(physicality: string | null): boolean {
  return isSchematicNodePhysicality(physicality);
}

/** Description-only fallback for a row with no product match at all --
 *  the only signal available for a line like "Embed Digital Signage
 *  annual software license, per device 1-50" or "Fixings, shelves,
 *  blanks, rails and lacer bars for above", neither of which will ever
 *  have an av_product_profile_id (nothing in an AV catalogue to match a
 *  software subscription or a box of fixings against).
 *
 *  Mirrors, word for word, the budget importer's own
 *  isThirdPartySoftwareLine / isGenericBulkMaterialsLine
 *  (../quotes/budgetImport/parse.ts) so the import-review layer and the
 *  Design & Schematics layer never disagree about the same line -- not
 *  re-implemented as a fresh regex, duplicated intentionally rather than
 *  imported across the quotes/wip feature boundary the same way the rest
 *  of this codebase keeps those two features independent. Adds one more
 *  pattern neither of those covers: "to be supplied by client" /
 *  "client-supplied" / "by others" phrasing, distinct from
 *  isThirdPartySoftwareLine's reuse case ("existing ... re-use", client
 *  already owns it) -- this is "client will provide something new",
 *  never quoted before.
 *
 *  Never guesses ACTIVE_DEVICE/PASSIVE_DEVICE from text alone -- that
 *  distinction is only ever trusted from a real catalogue match
 *  (classifyEquipmentPhysicality below). A genuinely unmatched line
 *  matching none of these patterns returns null -- "no textual signal
 *  either way", not a classification. */
export function classifyLineDescription(description: string): EquipmentPhysicality | null {
  if (/\bannual\b[^.]{0,40}\b(licen[cs]e|subscription)\b/i.test(description)) return "software_licence";
  if (/\b(cables?|connectors?|consumables?|fixings?|sundries)\b/i.test(description)) return "consumable";
  // Narrow, noun-phrase patterns only -- never the bare word "mount", which
  // real active devices legitimately carry in their own name ("rack-mount
  // switch", "wall-mount display"). Found live on job 103/room 201: "1000mm
  // Ceiling Suspension pack for above display screen" matched none of the
  // patterns above or below and stayed a false "Not categorised" /
  // "Unassigned equipment" / "Power unknown" node -- the same bug class
  // this module exists to fix, missed because a mount-shaped line has no
  // av_product_profile_id to fall back on (nothing in an AV catalogue to
  // match a suspension kit against) and no keyword this function knew yet.
  if (/\b(suspension\s+(pack|kit)|mounting\s+(bracket|kit|plate)|wall[- ]mount(?:ing)?\s+bracket|ceiling[- ]mount(?:ing)?\s+bracket)\b/i.test(description)) {
    return "mounting";
  }

  const d = description.toLowerCase();
  const isClientReuse = d.includes("existing") && (d.includes("re-use") || d.includes("reuse") || d.includes("re use"));
  if (
    isClientReuse ||
    /\bsupplied\s+by\s+(the\s+)?client\b/i.test(description) ||
    /\bclient[- ]supplied\b/i.test(description) ||
    /\bclient\s+to\s+supply\b/i.test(description) ||
    /\bby\s+others\b/i.test(description)
  ) {
    return "client_supplied_reference";
  }
  return null;
}

/** The full classification a job_equipment_requirements row gets, in the
 *  same precedence app.classify_equipment_physicality (Postgres) and
 *  av_product_profiles.physicality follow -- this is the client-side
 *  mirror fetchRoomPortBoard actually runs, since neither PostgREST nor
 *  this codebase's `.select()` call can invoke a server-side function
 *  inline, and computing it live off already-fetched columns keeps this
 *  read-time and join-fresh (matching how category/power_method/
 *  equipment_role already work -- never frozen onto the row, always read
 *  off whatever the linked profile currently says):
 *
 *   1. A profile that has already been curated wins outright -- "ask
 *      once, fixed everywhere", the same rule power_method already
 *      follows.
 *   2. Failing that, the row's own description, checked against the same
 *      narrow, evidence-based vocabulary the budget importer already
 *      trusts (see classifyLineDescription).
 *   3. Failing that, an already-known accessory category (the one
 *      physicality signal every matched product already carries today).
 *   4. Failing that, a real catalogue match falls back to power_method's
 *      own passive/not-passive answer -- reusing it rather than asking
 *      the same question twice.
 *   5. Only when there is truly nothing to go on does this return
 *      UNKNOWN_PHYSICAL_DEVICE -- an honest "don't know" that keeps
 *      behaving exactly as an unmatched row does today (a real warning
 *      candidate), never a guess dressed up as a classification. */
export function classifyEquipmentPhysicality(input: {
  description: string;
  category: string | null;
  hasProfile: boolean;
  profilePhysicality: string | null;
  powerMethod: string | null;
}): EquipmentPhysicality {
  if (input.profilePhysicality) return input.profilePhysicality as EquipmentPhysicality;

  const fromText = classifyLineDescription(input.description);
  if (fromText) return fromText;

  if (input.category === "accessory") return "accessory";

  if (input.hasProfile) return input.powerMethod === "passive" ? "passive_device" : "active_device";

  return "unknown_physical_device";
}
